import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Cloud, Plus, LogOut, AlertTriangle, ExternalLink,
  CheckCircle2, LayoutDashboard, Settings, Bell,
  TrendingDown, DollarSign, Layers, ChevronRight, ChevronDown, Activity,
  Sun, Moon, PanelLeftClose, PanelLeftOpen, Heart, BarChart3, GitFork
} from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../hooks/useTheme';
import { CloudMark, Wordmark } from '../components/CloudFrugalLogo';
import CostSankey from '../components/CostSankey';
import TrendsView from '../components/dashboard/TrendsView';
import RecommendationsView from '../components/dashboard/RecommendationsView';

interface AWSAccount {
  id: string;
  role_arn: string;
  account_id: string;
  template_version: string;
  stack_id: string | null;
  created_at: string;
}

interface CostData {
  totalSpend: number;
  currency: string;
  period: string;
  serviceBreakdown?: Record<string, number>;
}

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

// AWS-accurate service colors (hex for precise control)
const SERVICE_COLORS_HEX: Record<string, string> = {
  'EC2':          '#EA580C', // AWS orange
  'RDS':          '#DC2626', // deep red
  'S3':           '#16A34A', // AWS green
  'Lambda':       '#4E7D5E', // violet
  'CloudFront':   '#0284C7', // sky blue
  'Elastic Kubernetes': '#0F3D2E', // indigo
  'ElastiCache':  '#3d7f58', // purple
  'Redshift':     '#BE185D', // pink-red
  'Glue':         '#D97706', // amber
  'EMR':          '#0D9488', // teal
  'Athena':       '#1a6b42', // light indigo
  'Kinesis':      '#2563EB', // blue
  'Data Transfer':'#64748B', // slate
  'CloudWatch':   '#475569', // slate-dark
  'Fargate':      '#4E7D5E', // violet
};

function getServiceColorHex(service: string): string {
  for (const [key, color] of Object.entries(SERVICE_COLORS_HEX)) {
    if (service.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return '#1a6b42';
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [awsAccounts, setAwsAccounts] = useState<AWSAccount[]>([]);
  const [costData, setCostData] = useState<CostData | null>(null);
  const [allCostData, setAllCostData] = useState<Record<string, CostData>>({});
  const [costExplorerDisabled, setCostExplorerDisabled] = useState(false);
  const [dataNotReady, setDataNotReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }

      try {
        const userRes = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userRes.data);

        const accountsRes = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/aws-accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAwsAccounts(accountsRes.data);

        if (accountsRes.data.length > 0) {
          // Fetch cost data for all accounts in parallel
          const costResults = await Promise.all(
            accountsRes.data.map(async (account: AWSAccount) => {
              try {
                const costRes = await axios.get(
                  `${import.meta.env.VITE_API_BASE_URL}/api/costs/aws-accounts/${account.id}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                return { accountId: account.id, data: costRes.data, error: null };
              } catch (costErr: any) {
                return { accountId: account.id, data: null, error: costErr };
              }
            })
          );
          const newAllCostData: Record<string, CostData> = {};
          costResults.forEach(r => { if (r.data) newAllCostData[r.accountId] = r.data; });
          setAllCostData(newAllCostData);
          // First account with data for backward compat
          const first = costResults.find(r => r.data);
          if (first?.data) {
            setCostData(first.data);
          } else {
            const firstErr = costResults[0]?.error;
            if (firstErr) {
              const code = firstErr.response?.data?.code;
              if (code === 'COST_EXPLORER_NOT_ENABLED') setCostExplorerDisabled(true);
              else if (code === 'DATA_NOT_READY' || firstErr.response?.status === 202) setDataNotReady(true);
              else setError(firstErr.response?.data?.message || 'Failed to load cost data');
            }
          }
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load dashboard');
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const [latestTemplateVersion, setLatestTemplateVersion] = useState<string | null>(null);
  const [accountsExpanded, setAccountsExpanded] = useState(true);
  const [perAccountExpanded, setPerAccountExpanded] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/aws-accounts/latest-version`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setLatestTemplateVersion(r.data.version)).catch(() => {});
  }, []);

  const [view, setView] = useState<'overview' | 'accounts' | 'pricing' | 'costflow' | 'trends' | 'recommendations'>('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  const userInitial = user?.email?.[0]?.toUpperCase() || '?';
  const accountId = awsAccounts[0]?.account_id || awsAccounts[0]?.role_arn?.split(':')?.[4] || '—';

  // Aggregate across all accounts
  const totalAllAccounts = Object.values(allCostData).reduce((sum, d) => sum + Number(d.totalSpend), 0);
  const aggregatedServices: Record<string, number> = {};
  Object.values(allCostData).forEach(d => {
    Object.entries(d.serviceBreakdown || {}).forEach(([k, v]) => {
      aggregatedServices[k] = (aggregatedServices[k] || 0) + v;
    });
  });
  const sortedServices = Object.entries(aggregatedServices).sort(([, a], [, b]) => b - a).slice(0, 8);
  const maxCost = sortedServices[0]?.[1] || 1;
  const estimatedSavings = totalAllAccounts * 0.28;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4 animate-pulse">
            <CloudMark size={56} primaryColor={isDark ? '#ffffff' : '#0F3D2E'} midColor={isDark ? 'rgba(255,255,255,0.5)' : '#4E7D5E'} />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex">

      {/* Sidebar */}
      <aside
        className="flex-shrink-0 flex flex-col overflow-hidden"
        style={{
          width: collapsed ? '64px' : '260px',
          transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
          background: isDark ? '#0F172A' : '#FAFBFC',
          borderRight: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}`,
        }}
      >
        {/* Logo + collapse toggle */}
        <div className="px-3 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
          {!collapsed && (
            <div className="flex items-center gap-2 pl-2">
              <CloudMark size={40} primaryColor={isDark ? '#ffffff' : '#0F3D2E'} midColor={isDark ? 'rgba(255,255,255,0.5)' : '#4E7D5E'} />
              <Wordmark size={13} variant={isDark ? 'dark' : 'light'} />
            </div>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={collapsed ? { margin: '0 auto' } : undefined}
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-1">
          {[
            { icon: <LayoutDashboard className="w-4 h-4 flex-shrink-0" />, label: 'Overview', active: view === 'overview', onClick: () => setView('overview') },
            { icon: <Layers className="w-4 h-4 flex-shrink-0" />, label: 'AWS Accounts', active: view === 'accounts', onClick: () => awsAccounts.length === 0 ? navigate('/connect-account') : setView('accounts'), badge: String(awsAccounts.length) },
            { icon: <Activity className="w-4 h-4 flex-shrink-0" />, label: 'Cost Trends', active: view === 'trends', onClick: () => setView('trends') },
            { icon: <TrendingDown className="w-4 h-4 flex-shrink-0" />, label: 'Recommendations', active: view === 'recommendations', onClick: () => setView('recommendations') },
            { icon: <GitFork className="w-4 h-4 flex-shrink-0" />, label: 'Cost Flow', active: view === 'costflow', onClick: () => setView('costflow') },
            { icon: <Heart className="w-4 h-4 flex-shrink-0" />, label: 'Pricing', active: view === 'pricing', onClick: () => setView('pricing') },
          ].map(item => (
            <button
              key={item.label}
              onClick={item.onClick}
              title={collapsed ? item.label : undefined}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-colors"
              style={item.active ? { background: isDark ? 'rgba(15,61,46,0.14)' : 'rgba(15,61,46,0.07)', color: '#0F3D2E' } : { color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,22,32,0.55)' }}
            >
              {item.icon}
              {!collapsed && (
                <>
                  <span>{item.label}</span>
                  {item.badge !== undefined && (
                    <span className="ml-auto text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded-full">{item.badge}</span>
                  )}
                </>
              )}
            </button>
          ))}
        </nav>

        {/* User section — hover entire container to reveal actions */}
        <div
          className="border-t border-gray-200 dark:border-gray-800"
          onMouseEnter={() => setUserMenuOpen(true)}
          onMouseLeave={() => setUserMenuOpen(false)}
        >
          {/* Slide-in actions */}
          <div
            className="overflow-hidden px-2 space-y-1"
            style={{ maxHeight: userMenuOpen ? '220px' : '0px', transition: 'max-height 0.2s ease', paddingTop: userMenuOpen ? '8px' : '0' }}
          >
            <a href="/" title={collapsed ? 'Homepage' : undefined}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 font-medium text-sm transition-colors"
            >
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
              {!collapsed && 'Homepage'}
            </a>
            <button onClick={() => navigate('/settings')} title={collapsed ? 'Settings' : undefined}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 font-medium text-sm transition-colors"
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
              {!collapsed && 'Settings'}
            </button>
            <button onClick={toggle} title={collapsed ? (theme === 'dark' ? 'Light mode' : 'Dark mode') : undefined}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 font-medium text-sm transition-colors"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4 flex-shrink-0" /> : <Moon className="w-4 h-4 flex-shrink-0" />}
              {!collapsed && (theme === 'dark' ? 'Light mode' : 'Dark mode')}
            </button>
            <button onClick={handleLogout} title={collapsed ? 'Sign Out' : undefined}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 font-medium text-sm transition-colors"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!collapsed && 'Sign Out'}
            </button>
          </div>

          {/* Always-visible avatar */}
          <div className="px-3 py-3 flex items-center gap-3 cursor-default">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0F3D2E, #4E7D5E)' }}>
              {userInitial}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-900 dark:text-white font-medium truncate">{user?.email}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">Free plan</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top bar */}
        <header className="px-8 py-4 flex items-center justify-between flex-shrink-0" style={{
          background: isDark ? 'rgba(17,24,39,0.8)' : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}`,
        }}>
          <div>
            <h1 style={{ fontSize: '18px', fontWeight: 700, color: isDark ? '#F1F5F9' : '#0F172A', fontFamily: FONT, letterSpacing: '-0.01em' }}>
              {view === 'accounts' ? 'AWS Accounts' : view === 'pricing' ? 'Pricing' : view === 'costflow' ? 'Cost Flow' : view === 'trends' ? 'Cost Trends' : view === 'recommendations' ? 'Recommendations' : 'Overview'}
            </h1>
            <p style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.4)', marginTop: '2px', fontFamily: FONT }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors" style={{
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'}`,
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.4)',
            }}>
              <Bell className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/connect-account')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:shadow-lg"
              style={{ background: '#0F3D2E', color: '#ffffff', boxShadow: '0 2px 8px rgba(15,61,46,0.25)' }}
            >
              <Plus className="w-4 h-4" />
              Add Account
            </button>
          </div>
        </header>

        {/* Body */}
        <main className="flex-1 overflow-auto px-8 py-8 space-y-6">

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Accounts view */}
          {view === 'accounts' && (
            <div className="space-y-4">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">{awsAccounts.length} account{awsAccounts.length !== 1 ? 's' : ''} connected</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {awsAccounts.map(account => {
                  const acctId = account.account_id || account.role_arn?.split(':')?.[4] || '—';
                  const region = account.role_arn?.split(':')?.[3] || 'global';
                  const roleName = account.role_arn?.split('/')?.[1] || '—';
                  const connectedDate = new Date(account.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                  return (
                    <div key={account.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                            <img src="/aws-icons/ec2.svg" alt="AWS" className="w-6 h-6 object-contain" />
                          </div>
                          <div>
                            <p className="text-gray-900 dark:text-white text-sm font-bold font-mono">{acctId}</p>
                            <p className="text-gray-400 dark:text-gray-500 text-xs">AWS Account ID</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                          <span className="text-green-600 dark:text-green-400 text-xs font-semibold">Active</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                        <div>
                          <p className="text-gray-400 dark:text-gray-500 text-xs mb-1">IAM Role</p>
                          <p className="text-gray-700 dark:text-gray-300 text-xs font-mono truncate">{roleName}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 dark:text-gray-500 text-xs mb-1">Region</p>
                          <p className="text-gray-700 dark:text-gray-300 text-xs font-mono">{region}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 dark:text-gray-500 text-xs mb-1">Connected</p>
                          <p className="text-gray-700 dark:text-gray-300 text-xs">{connectedDate}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 dark:text-gray-500 text-xs mb-1">Access</p>
                          <p className="text-gray-700 dark:text-gray-300 text-xs flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 text-green-500" /> Read-only IAM
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-400 dark:text-gray-500 text-xs mb-1">Template</p>
                          <p className="text-gray-700 dark:text-gray-300 text-xs font-mono">v{account.template_version || '1.0'}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* No accounts state */}
          {view === 'overview' && awsAccounts.length === 0 ? (
            <div className="flex items-center justify-center h-full min-h-96">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 rounded-2xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center mx-auto mb-6">
                  <Cloud className="w-10 h-10 text-gray-400 dark:text-gray-600" />
                </div>
                <h2 className="text-gray-900 dark:text-white text-2xl font-bold mb-3">Connect your first AWS account</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-8 leading-relaxed">
                  Link your AWS account via a read-only IAM role. Takes 2 minutes — no credit card needed.
                </p>
                <button
                  onClick={() => navigate('/connect-account')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:shadow-lg"
                  style={{ background: '#0F3D2E', color: '#ffffff', boxShadow: '0 2px 8px rgba(15,61,46,0.25)' }}
                >
                  <Plus className="w-5 h-5" />
                  Connect AWS Account
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : view === 'overview' ? (
            <>
              {/* Banners */}
              {dataNotReady && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-blue-300 font-semibold text-sm">Data is being prepared</p>
                    <p className="text-blue-400/80 text-xs mt-0.5">
                      AWS takes up to 24 hours to ingest Cost Explorer data after first activation. Check back tomorrow.
                    </p>
                  </div>
                </div>
              )}

              {costExplorerDisabled && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5">
                  <div className="flex items-start gap-4 mb-4">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-300 font-semibold text-sm">Cost Explorer not enabled</p>
                      <p className="text-amber-400/80 text-xs mt-0.5">
                        Enable it in your AWS account to start seeing cost data. One-time setup, takes under a minute.
                      </p>
                    </div>
                  </div>
                  <div className="ml-9 space-y-2">
                    <ol className="text-xs text-amber-400/70 space-y-1 list-decimal list-inside">
                      <li>Log into AWS account <span className="font-mono bg-amber-500/10 px-1 rounded">{accountId}</span></li>
                      <li>Go to Billing → Cost Explorer</li>
                      <li>Click <strong className="text-amber-300">Enable Cost Explorer</strong></li>
                      <li>Data appears here within 24 hours</li>
                    </ol>
                    <a
                      href="https://console.aws.amazon.com/cost-management/home#/cost-explorer"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 font-medium mt-2 transition-colors"
                    >
                      Open AWS Console <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              )}

              {/* Stat cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    label: 'Total Spend',
                    value: totalAllAccounts > 0 ? `$${totalAllAccounts.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—',
                    sub: totalAllAccounts > 0 ? `${Object.keys(allCostData).length} account${Object.keys(allCostData).length !== 1 ? 's' : ''} · current period` : 'No data yet',
                    iconColor: '#0F3D2E',
                    iconBg: 'rgba(15,61,46,0.1)',
                    icon: <DollarSign className="w-4 h-4" />,
                  },
                  {
                    label: 'Potential Savings',
                    value: estimatedSavings > 0 ? `$${estimatedSavings.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—',
                    sub: 'Est. 28% via RIs & rightsizing',
                    iconColor: '#16A34A',
                    iconBg: 'rgba(22,163,74,0.1)',
                    icon: <TrendingDown className="w-4 h-4" />,
                    valueColor: '#16A34A',
                  },
                  {
                    label: 'Biggest Spender',
                    value: sortedServices[0]?.[0]?.replace('Amazon ', '').replace('AWS ', '') || '—',
                    sub: sortedServices[0] ? `$${Number(sortedServices[0][1]).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} this period` : 'No data',
                    iconColor: '#4E7D5E',
                    iconBg: 'rgba(124,58,237,0.1)',
                    icon: <Activity className="w-4 h-4" />,
                    small: true,
                  },
                  {
                    label: 'AWS Accounts',
                    value: String(awsAccounts.length),
                    sub: 'Connected · read-only',
                    iconColor: '#0284C7',
                    iconBg: 'rgba(2,132,199,0.1)',
                    icon: <Layers className="w-4 h-4" />,
                  },
                ].map(card => (
                  <div key={card.label} style={{
                    background: isDark ? 'rgba(17,24,39,0.8)' : '#ffffff',
                    borderRadius: '16px',
                    padding: '22px 22px 20px',
                    boxShadow: isDark ? '0 1px 2px rgba(0,0,0,0.2)' : '0 1px 2px rgba(0,0,0,0.03), 0 4px 20px rgba(0,0,0,0.025)',
                    border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,22,32,0.05)',
                    fontFamily: FONT,
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.04)';
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.boxShadow = isDark ? '0 1px 2px rgba(0,0,0,0.2)' : '0 1px 2px rgba(0,0,0,0.03), 0 4px 20px rgba(0,0,0,0.025)';
                    (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                  }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.45)', letterSpacing: '0.02em', textTransform: 'uppercase' }}>{card.label}</span>
                      <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: card.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: card.iconColor, flexShrink: 0 }}>
                        {card.icon}
                      </div>
                    </div>
                    <div style={{ fontSize: card.small ? '20px' : '28px', fontWeight: 700, color: card.valueColor || (isDark ? '#F1F5F9' : '#0F172A'), lineHeight: 1.1, marginBottom: '6px', letterSpacing: '-0.01em' }}>
                      {card.value}
                    </div>
                    <div style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.45)' }}>{card.sub}</div>
                  </div>
                ))}
              </div>

              {/* Per-account spend cards */}
              {awsAccounts.length > 1 && (
                <div>
                  <button
                    onClick={() => setPerAccountExpanded(e => !e)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 10px', fontFamily: FONT }}
                  >
                    <ChevronDown className="w-3.5 h-3.5" style={{ color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,22,32,0.35)', transform: perAccountExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }} />
                    <span style={{ fontSize: '11px', fontWeight: 500, color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,22,32,0.38)' }}>
                      Per-account breakdown {!perAccountExpanded && `(${awsAccounts.length} accounts)`}
                    </span>
                  </button>
                  {perAccountExpanded && <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {awsAccounts.map((account, idx) => {
                    const data = allCostData[account.id];
                    const spend = data ? Number(data.totalSpend) : null;
                    const pct = spend && totalAllAccounts > 0 ? (spend / totalAllAccounts) * 100 : 0;
                    const topSvc = data?.serviceBreakdown
                      ? Object.entries(data.serviceBreakdown).sort(([, a], [, b]) => b - a)[0]
                      : null;
                    const accentColors = ['#0F3D2E', '#4E7D5E', '#0284C7'];
                    const accent = accentColors[idx % accentColors.length];
                    return (
                      <div key={account.id} style={{
                        background: isDark ? 'rgba(17,24,39,0.8)' : '#ffffff',
                        borderRadius: '16px',
                        padding: '18px 20px',
                        boxShadow: isDark ? 'none' : '0 1px 3px rgba(15,22,32,0.06), 0 4px 16px rgba(15,22,32,0.04)',
                        border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,22,32,0.06)',
                        borderTop: `3px solid ${accent}`,
                        fontFamily: FONT,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, fontFamily: 'ui-monospace, monospace', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,22,32,0.5)' }}>
                            {account.account_id || account.id}
                          </span>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 8px', borderRadius: '20px', background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.2)' }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16A34A', flexShrink: 0 }} />
                            <span style={{ fontSize: '11px', fontWeight: 500, color: '#16A34A' }}>Active</span>
                          </div>
                        </div>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: isDark ? '#fff' : '#07281D', marginBottom: '6px', lineHeight: 1.1 }}>
                          {spend !== null ? `$${spend.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <span style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.38)' : 'rgba(15,22,32,0.45)' }}>
                            {topSvc ? topSvc[0].replace('Amazon ', '').replace('AWS ', '') : 'No data'}
                          </span>
                          {pct > 0 && (
                            <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: `${accent}14`, color: accent }}>
                              {pct.toFixed(0)}%
                            </span>
                          )}
                        </div>
                        {spend !== null && totalAllAccounts > 0 && (
                          <div style={{ height: '4px', borderRadius: '2px', background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,22,32,0.08)' }}>
                            <div style={{ height: '100%', borderRadius: '2px', width: `${pct}%`, background: accent, opacity: 0.75, transition: 'width 0.6s ease' }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>}
                </div>
              )}

              {/* Service breakdown + accounts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Service breakdown */}
                <div style={{
                  background: isDark ? 'rgba(17,24,39,0.8)' : '#ffffff',
                  borderRadius: '16px',
                  padding: '22px 24px',
                  boxShadow: isDark ? 'none' : '0 1px 3px rgba(15,22,32,0.06), 0 4px 16px rgba(15,22,32,0.04)',
                  border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,22,32,0.06)',
                  fontFamily: FONT,
                }}>
                  <h2 style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#fff' : '#07281D', marginBottom: '20px' }}>Cost by Service</h2>
                  {sortedServices.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {sortedServices.map(([service, cost]) => {
                        const pct = (cost / maxCost) * 100;
                        const totalPct = totalAllAccounts > 0 ? ((cost / totalAllAccounts) * 100).toFixed(1) : '0';
                        const color = getServiceColorHex(service);
                        const shortName = service.replace('Amazon ', '').replace('AWS ', '');
                        return (
                          <div key={service}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: color, flexShrink: 0 }} />
                                <span style={{ fontSize: '12px', fontWeight: 500, color: isDark ? 'rgba(255,255,255,0.75)' : 'rgba(15,22,32,0.75)' }}>{shortName}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '11px', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(15,22,32,0.35)' }}>{totalPct}%</span>
                                <span style={{ fontSize: '12px', fontWeight: 600, fontFamily: 'ui-monospace, monospace', color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(15,22,32,0.65)' }}>
                                  ${Number(cost).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </span>
                              </div>
                            </div>
                            <div style={{ height: '6px', background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,22,32,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: '3px', width: `${pct}%`, background: color, opacity: 0.85, transition: 'width 0.7s ease' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '160px', textAlign: 'center' }}>
                      <Activity className="w-8 h-8 mb-3" style={{ color: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(15,22,32,0.15)' }} />
                      <p style={{ fontSize: '13px', color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,22,32,0.35)' }}>No cost data available yet</p>
                    </div>
                  )}
                </div>

                {/* Connected accounts */}
                <div style={{
                  background: isDark ? 'rgba(17,24,39,0.8)' : '#ffffff',
                  borderRadius: '16px',
                  padding: '22px 24px',
                  boxShadow: isDark ? 'none' : '0 1px 3px rgba(15,22,32,0.06), 0 4px 16px rgba(15,22,32,0.04)',
                  border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,22,32,0.06)',
                  fontFamily: FONT,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: accountsExpanded ? '18px' : '0' }}>
                    <button
                      onClick={() => setAccountsExpanded(e => !e)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: FONT }}
                    >
                      <ChevronDown className="w-4 h-4" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,22,32,0.4)', transform: accountsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }} />
                      <h2 style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#fff' : '#07281D' }}>
                        Connected Accounts
                        <span style={{ fontSize: '11px', fontWeight: 400, color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,22,32,0.38)', marginLeft: '6px' }}>({awsAccounts.length})</span>
                      </h2>
                    </button>
                    <button onClick={() => navigate('/connect-account')} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 500, color: '#0F3D2E', background: 'rgba(15,61,46,0.08)', border: '1px solid rgba(15,61,46,0.15)', borderRadius: '8px', padding: '4px 10px', cursor: 'pointer', fontFamily: FONT }}>
                      <Plus className="w-3.5 h-3.5" /> Add
                    </button>
                  </div>
                  {accountsExpanded && <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {awsAccounts.map((account, idx) => {
                      const acctId = account.account_id || account.role_arn?.split(':')?.[4] || '—';
                      const region = account.role_arn?.split(':')?.[3] || 'us-east-1';
                      const roleName = account.role_arn?.split('/')?.[1] || '—';
                      const connectedDate = new Date(account.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                      const acctSpend = allCostData[account.id] ? Number(allCostData[account.id].totalSpend) : null;
                      const accentColors = ['#0F3D2E', '#4E7D5E', '#0284C7'];
                      const accent = accentColors[idx % accentColors.length];
                      return (
                        <div key={account.id} style={{ padding: '14px 16px', borderRadius: '12px', background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,22,32,0.025)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,22,32,0.07)'}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `${accent}15`, border: `1px solid ${accent}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Cloud className="w-4 h-4" style={{ color: accent }} />
                              </div>
                              <div>
                                <p style={{ fontSize: '12px', fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: isDark ? '#fff' : '#07281D' }}>{acctId}</p>
                                {acctSpend !== null && (
                                  <p style={{ fontSize: '11px', color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,22,32,0.45)' }}>
                                    ${acctSpend.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} this period
                                  </p>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {latestTemplateVersion && account.template_version && account.template_version !== latestTemplateVersion && (() => {
                                const templateUrl = 'https://cloudfrugal-templates.s3.amazonaws.com/cloudfrugal-role.yaml';
                                const cfnUrl = account.stack_id
                                  ? `https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/update/template?stackId=${encodeURIComponent(account.stack_id)}&templateURL=${encodeURIComponent(templateUrl)}`
                                  : `https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/update/template?templateURL=${encodeURIComponent(templateUrl)}`;
                                return (
                                  <a href={cfnUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', fontWeight: 600, padding: '3px 8px', borderRadius: '6px', background: 'rgba(217,119,6,0.1)', color: '#d97706', border: '1px solid rgba(217,119,6,0.25)', textDecoration: 'none' }}>
                                    ↑ v{account.template_version}→{latestTemplateVersion}
                                  </a>
                                );
                              })()}
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 8px', borderRadius: '20px', background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.2)' }}>
                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16A34A' }} />
                                <span style={{ fontSize: '11px', fontWeight: 500, color: '#16A34A' }}>Active</span>
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', paddingTop: '10px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,22,32,0.06)'}` }}>
                            {[
                              { label: 'IAM Role', value: roleName, mono: true },
                              { label: 'Region', value: region || 'global', mono: true },
                              { label: 'Connected', value: connectedDate, mono: false },
                              { label: 'Access', value: 'Read-only', mono: false },
                            ].map(item => (
                              <div key={item.label}>
                                <p style={{ fontSize: '10px', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(15,22,32,0.35)', marginBottom: '1px' }}>{item.label}</p>
                                <p style={{ fontSize: '11px', fontFamily: item.mono ? 'ui-monospace, monospace' : FONT, color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,22,32,0.65)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>}
                </div>
              </div>

            </>
          ) : null}

          {/* Trends view */}
          {view === 'trends' && (
            <TrendsView accounts={awsAccounts} isDark={isDark} />
          )}

          {/* Recommendations view */}
          {view === 'recommendations' && (
            <RecommendationsView allCostData={allCostData} accounts={awsAccounts} isDark={isDark} />
          )}

          {/* Cost Flow view */}
          {view === 'costflow' && (
            <div>
              {Object.keys(aggregatedServices).length > 0 ? (
                <div>
                  <p className="text-sm mb-6" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,22,32,0.45)', fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}>
                    Visualising how your <span style={{ color: isDark ? '#E8B84C' : '#0F3D2E', fontWeight: 600 }}>${totalAllAccounts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> flows across AWS categories and services (all accounts). Hover nodes and links for details.
                  </p>
                  <div className="rounded-2xl overflow-hidden p-6" style={{ background: isDark ? 'rgba(255,255,255,0.03)' : 'white', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,61,46,0.1)' }}>
                    <CostSankey
                      serviceBreakdown={aggregatedServices}
                      totalSpend={totalAllAccounts}
                      isDark={isDark}
                    />
                  </div>
                  {/* Category legend */}
                  <div className="flex flex-wrap gap-3 mt-6">
                    {[
                      { label: 'Compute',       color: '#FF9900' },
                      { label: 'Storage',        color: '#7AA116' },
                      { label: 'Database',       color: '#C7131F' },
                      { label: 'Networking',     color: '#8C4FFF' },
                      { label: 'Serverless',     color: '#E7157B' },
                      { label: 'ML & Analytics', color: '#01A88D' },
                      { label: 'Management',     color: '#232F3E' },
                      { label: 'Other',          color: '#6B7280' },
                    ].map(cat => (
                      <div key={cat.label} className="flex items-center gap-1.5">
                        <div style={{ width: '10px', height: '10px', borderRadius: '3px', background: cat.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '12px', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,22,32,0.5)', fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}>{cat.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <GitFork className="w-10 h-10 mb-4 opacity-20" style={{ color: isDark ? '#fff' : '#07281D' }} />
                  <p className="font-semibold mb-1" style={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,22,32,0.5)' }}>No cost data yet</p>
                  <p className="text-sm" style={{ color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(15,22,32,0.3)' }}>Connect an AWS account to see your spend flow</p>
                </div>
              )}
            </div>
          )}

          {/* Pricing view */}
          {view === 'pricing' && (() => {
            const totalSpend = costData ? Number(costData.totalSpend) : 0;
            // Estimated savings = 20% of spend (conservative rightsizing estimate)
            const estimatedSavings = totalSpend * 0.20;
            const paymentDue = estimatedSavings * 0.025; // 2.5% of savings

            return (
              <div className="space-y-8">

                {/* ── TIPS ───────────────────────────────────────────────── */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Heart className="w-4 h-4" style={{ color: '#4E7D5E' }} />
                    <h2 className="text-gray-900 dark:text-white font-semibold text-sm uppercase tracking-wide">Tips</h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    {/* Tip action */}
                    <div className="bg-white dark:bg-gray-900 rounded-2xl p-6" style={{ border: '1px solid rgba(15,61,46,0.2)', boxShadow: '0 1px 3px rgba(15,61,46,0.06)' }}>
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Tip Jar</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-5">
                        Voluntary. No paywalls, ever. If CloudFrugal saves you money, a coffee tip goes a long way for a small team.
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {[
                          { label: '$5', highlight: false },
                          { label: '$10', highlight: true },
                          { label: '$25', highlight: false },
                          { label: 'Custom', highlight: false },
                        ].map(({ label, highlight }) => (
                          <button
                            key={label}
                            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:-translate-y-0.5"
                            style={highlight
                              ? { background: '#0F3D2E', color: '#ffffff', border: '1px solid #0F3D2E', boxShadow: '0 2px 8px rgba(15,61,46,0.25)' }
                              : { background: 'transparent', color: '#0F3D2E', border: '1px solid rgba(15,61,46,0.2)' }}
                          >
                            {label}
                            {highlight && <span className="ml-1 text-xs opacity-60">popular</span>}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500">Your call. No pressure. Ever.</p>
                    </div>

                    {/* Tip history */}
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-4">Tip History</p>
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(15,61,46,0.06)' }}>
                          <Heart className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                        </div>
                        <p className="text-sm text-gray-400 dark:text-gray-500">No tips yet</p>
                        <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">Your contributions will appear here.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── PAYMENTS ───────────────────────────────────────────── */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-4 h-4" style={{ color: '#0F3D2E' }} />
                    <h2 className="text-gray-900 dark:text-white font-semibold text-sm uppercase tracking-wide">Payments</h2>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    {/* Payment due */}
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">Current Period</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
                        Based on 2.5% of estimated savings identified this period. Only pay after you've acted on recommendations.
                      </p>
                      <div className="rounded-xl p-4 mb-5 text-xs font-mono" style={{ background: '#0F172A' }}>
                        <div className="space-y-2.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          <div className="flex justify-between">
                            <span>Your AWS spend</span>
                            <span className="text-white">{totalSpend > 0 ? `$${totalSpend.toFixed(2)}` : '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Est. savings identified (20%)</span>
                            <span className="text-white">{estimatedSavings > 0 ? `$${estimatedSavings.toFixed(2)}` : '—'}</span>
                          </div>
                          <div className="flex justify-between pt-2.5 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                            <span>Our ask (2.5%)</span>
                            <span className="font-bold" style={{ color: '#E8B84C' }}>
                              {paymentDue > 0 ? `$${paymentDue.toFixed(2)}` : '—'}
                            </span>
                          </div>
                        </div>
                        <p className="font-sans mt-3 text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
                          No savings identified? No ask. Ever.
                        </p>
                      </div>
                      <button
                        className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all hover:shadow-lg"
                        style={paymentDue > 0
                          ? { background: '#0F3D2E', color: '#fff', boxShadow: '0 2px 8px rgba(15,61,46,0.25)' }
                          : { background: 'rgba(15,61,46,0.06)', color: 'rgba(15,22,32,0.3)', cursor: 'not-allowed' }}
                        disabled={paymentDue === 0}
                      >
                        {paymentDue > 0 ? `Pay $${paymentDue.toFixed(2)}` : 'Nothing due yet'}
                      </button>
                    </div>

                    {/* Payment history */}
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6">
                      <p className="text-sm font-bold text-gray-900 dark:text-white mb-4">Payment History</p>
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: 'rgba(15,61,46,0.06)' }}>
                          <BarChart3 className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                        </div>
                        <p className="text-sm text-gray-400 dark:text-gray-500">No payments yet</p>
                        <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">Payments you make will be tracked here.</p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            );
          })()}

          {/* Tip Jar — always visible in overview */}
          {view === 'overview' && (
            <div className="rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
              style={{ background: isDark ? 'rgba(15,61,46,0.06)' : 'rgba(15,61,46,0.03)', border: `1px solid ${isDark ? 'rgba(15,61,46,0.15)' : 'rgba(15,61,46,0.1)'}` }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(15,61,46,0.1)' }}>
                <Heart className="w-4 h-4" style={{ color: '#0F3D2E' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold mb-0.5" style={{ color: isDark ? '#fff' : '#0F172A', fontFamily: FONT }}>Enjoying CloudFrugal?</p>
                <p className="text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.5)', fontFamily: FONT }}>
                  We're a small team building something honest. If we save you money, consider tipping — no paywalls, ever.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                {['$5', '$10', '$25'].map(a => (
                  <button key={a}
                    className="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all hover:-translate-y-0.5"
                    style={a === '$10'
                      ? { background: '#0F3D2E', color: '#ffffff', boxShadow: '0 1px 4px rgba(15,61,46,0.3)' }
                      : { background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,61,46,0.05)', color: '#0F3D2E', border: `1px solid ${isDark ? 'rgba(15,61,46,0.2)' : 'rgba(15,61,46,0.15)'}` }}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
